import React, { FC, ReactElement, useEffect, useRef, useState } from 'react'
import AppButton from '@components/shared/appButton'

interface FloatingActionButtonProps {
  editorRef: React.RefObject<HTMLTextAreaElement>
  onClick: ((text: string) => Promise<void>) | ((text: string) => void)
  buttonChild: ReactElement
  disabled?: boolean
}

const FloatingActionButton: FC<FloatingActionButtonProps> = ({
  editorRef,
  onClick,
  buttonChild,
  disabled,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [buttonVisible, setButtonVisible] = useState(false)
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 })
  const [buttonLoading, setButtonLoading] = useState<boolean>(false)

  useEffect(() => {
    let mouseX = 0
    let mouseY = 0
    const editor = editorRef.current

    const handleMouseMove = (event: MouseEvent) => {
      // Adjust mouse coordinates relative to the textarea element
      const rect = editor?.getBoundingClientRect()
      const userOffsetX = 20
      const userOffsetY = -30
      mouseX = event.clientX - (rect ? rect.left : 0) + userOffsetX
      mouseY = event.clientY - (rect ? rect.top : 0) + userOffsetY
    }

    const handleSelectionChange = () => {
      if (!editor) return

      const start = editor.selectionStart
      const end = editor.selectionEnd

      // Condition to check if selection is inside the textarea
      const rect = editor.getBoundingClientRect()
      const selectionInsideEditor =
        mouseX >= 0 &&
        mouseX <= rect.width &&
        mouseY >= 0 &&
        mouseY <= rect.height

      if (start !== end && selectionInsideEditor) {
        setButtonPosition({ top: mouseY, left: mouseX })
        setButtonVisible(true)
      } else {
        setButtonVisible(false)
      }
    }

    const handleClickOutside = (event: MouseEvent) => {
      const button = buttonRef.current
      if (
        (!editor || !editor.contains(event.target as Node)) &&
        (!button || !button.contains(event.target as Node))
      ) {
        setButtonVisible(false)
      }
    }

    const handleEditorChange = () => {
      setButtonVisible(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('selectionchange', handleSelectionChange)
    document.addEventListener('click', handleClickOutside)
    document.addEventListener('input', handleEditorChange)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('selectionchange', handleSelectionChange)
      document.removeEventListener('click', handleClickOutside)
      document.removeEventListener('input', handleEditorChange)
    }
  }, [editorRef])

  const onButtonClick = () => {
    const editor = editorRef.current
    if (editor) {
      const selectedText = editor.value.substring(
        editor.selectionStart,
        editor.selectionEnd
      )
      const result = onClick(selectedText)
      if (result instanceof Promise) {
        setButtonLoading(true)
        result
          .then(() => setButtonVisible(false))
          .finally(() => setButtonLoading(false))
      } else {
        setButtonVisible(false)
      }
    }
  }

  return (
    <>
      {(buttonVisible || buttonLoading) && (
        <AppButton
          reference={buttonRef}
          style={{
            position: 'absolute',
            top: `${buttonPosition.top}px`,
            left: `${buttonPosition.left}px`,
          }}
          disabled={disabled}
          buttonChild={buttonChild}
          onClick={onButtonClick}
          isLoading={buttonLoading}
        />
      )}
    </>
  )
}

export default FloatingActionButton
